import 'react-phone-number-input/style.css';

import { useField, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import {
  Country,
  getCountries,
  getCountryCallingCode as getCallingCode,
} from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';

import styled from '@emotion/styled';

import DownArrowIcon from '../Icons/DownArrowIcon';
import { FieldErrorInfo, InputLabel, InputWrapper } from './FormField';

interface PhoneNumberInputFieldProps {
  label?: string;
  name: string;
  id?: string;
  placeholder?: string;
  className?: string;
  interactionFn?: (value: string) => void;
  fixedCountry?: Country; // Optional prop to fix the country selection
  maxLength?: number; // Optional prop to limit the length of the phone number input
}

const CustomPhoneNumberInputField: React.FC<PhoneNumberInputFieldProps> = ({
  label,
  name,
  id,
  placeholder,
  className,
  interactionFn,
  fixedCountry,
  maxLength,
}) => {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField<string>(name);
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    fixedCountry || 'NG',
  );

  const countries = fixedCountry ? [fixedCountry] : getCountries();
  const countryCode = getCallingCode(selectedCountry);

  useEffect(() => {
    if (fixedCountry) {
      setSelectedCountry(fixedCountry);
    }
  }, [fixedCountry]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (fixedCountry) return;

    const country = e.target.value as Country;
    const newCountryCode = getCallingCode(country);

    // Extract the number part without the old country code
    const currentNumberPart = getNumberPart();

    setSelectedCountry(country);

    // Update the full phone number with new country code and existing number
    const newFullNumber = currentNumberPart
      ? `+${newCountryCode}${currentNumberPart}`
      : '';
    setFieldValue(name, newFullNumber);
    if (interactionFn) interactionFn(newFullNumber);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedPhoneNumber = e.target.value.replace(/\D/g, ''); // Remove non-digits
    const phoneNumber = maxLength
      ? sanitizedPhoneNumber.slice(0, maxLength)
      : sanitizedPhoneNumber;
    const fullNumber = phoneNumber ? `+${countryCode}${phoneNumber}` : '';

    setFieldValue(name, fullNumber);
    if (interactionFn) interactionFn(fullNumber);
  };

  // Extract just the number part from the full phone number
  const getNumberPart = () => {
    if (!field.value) return '';
    const fullNumber = field.value.replace(/\D/g, '');
    const countryCodeLength = countryCode.length;
    return fullNumber.slice(countryCodeLength);
  };

  return (
    <InputWrapper className={className}>
      {label && <InputLabel htmlFor={name || id}>{label}</InputLabel>}
      <PhoneInputContainer>
        <CountrySelector hasFixedCountry={Boolean(fixedCountry)}>
          <CountryFlag
            src={`https://flagcdn.com/w20/${selectedCountry.toLowerCase()}.png`}
            alt={selectedCountry}
          />
          <CountryCode>+{countryCode}</CountryCode>
          <HiddenCountrySelect
            value={selectedCountry}
            onChange={handleCountryChange}
            disabled={Boolean(fixedCountry)}
          >
            {countries
              .sort((a, b) => en[a].localeCompare(en[b]))
              .map((country) => (
                <option key={country} value={country}>
                  {en[country]} (+{getCallingCode(country)})
                </option>
              ))}
          </HiddenCountrySelect>
          {!fixedCountry && (
            <DropdownArrow>
              <DownArrowIcon />
            </DropdownArrow>
          )}
        </CountrySelector>

        <NumberInput
          id={id}
          type='tel'
          placeholder={placeholder || '70 0394 129'}
          value={getNumberPart()}
          onChange={handleNumberChange}
          maxLength={maxLength}
        />
      </PhoneInputContainer>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error.capitalize()}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default CustomPhoneNumberInputField;

const PhoneInputContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const CountrySelector = styled.div<{ hasFixedCountry: boolean }>`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  padding: 8px 12px;
  font-weight: 400;
  font-size: 14px;
  height: h0px;
  cursor: pointer;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: ${({ hasFixedCountry }) => (hasFixedCountry ? '100px' : '120px')};
  position: relative;

  &:focus-within {
    border-color: ${({ theme }) => theme.palette.logiratePrimary};
  }

  &:hover {
    cursor: pointer;
  }
`;

const CountryFlag = styled.img`
  width: 20px;
  height: 15px;
  margin-right: 8px;
`;

const CountryCode = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 400;
`;

const HiddenCountrySelect = styled.select`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
`;

const DropdownArrow = styled.span`
  font-size: 10px;
  color: #666;
  margin-left: 8px;
`;

const NumberInput = styled.input`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  padding: 12px;
  font-weight: 400;
  font-size: 14px;
  line-height: 14px;
  height: 40px;
  cursor: text;
  flex: 1;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.logiratePrimary};
  }

  &:hover {
    cursor: text;
  }

  &::placeholder {
    color: ${({ theme }) => theme.palette.darkestBorderColor};
    opacity: 1;
    font-weight: 400;
  }
`;
